import React, { useState, useEffect } from 'react';
import { Row, Col } from 'react-bootstrap';
import { Button } from 'react-bootstrap';
import Alert from 'react-bootstrap/Alert';
import { Preloader } from '../preloader';
import axios from 'axios';
import { useAxios } from '../../hooks/useAxios';
import { useSessionStorage } from '../../hooks/useSessionStorage';
import StatusMessages from '../recipe-status-messages';
import { getSampleFiles, SamepleElements } from '../../data/platform/sample_files_index';
import { Console } from '../../services/platform-api';
import { WorkflowConfig } from '../workflow-config';
import WFSettings from '../../data/platform/workflow_settings.json';

const InvoiceExtractionWorkFlow = ({
  workato_recipe_id,
  combination_id,
  connectors,
  triggers,
  activeTab,
  arc_tab_ref1,
  arc_tab_ref2,
  webhook_url,
  this_folder_id,
  this_sheet_id,
  email,
  connections_data,
  recipeSetupStatus,
  recipeActive,
  manual,
  connected,
  refreshTheRecipe,
  setRecipeDisabled,
}) => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [selectedFileName, setSelectedFileName] = useState('');
  const [settingUpRecipe, setSettingUpRecipe] = useState(true);
  const [workato_id] = useSessionStorage('workatoId', '');
  const { data: lookupTableData = null, fetchNow: checkWorkatoJobStatus } = useAxios();
  const [WJST_id] = useSessionStorage('WJST_id', 1);
  const [jobStatusIntervalId, setJobStatusIntervalId] = useState();
  const [resetStatuses, setResetStatuses] = useState(false);
  const [sampleFileName1, setSampleFileName1] = useState(null);
  const [sampleFile1, setSampleFile1] = useState(null);

  const [managersEmail, setManagersEmail] = useState('');
  const [approversEmail, setApproversEmail] = useState('');
  const [financeEmail, setFinanceEmail] = useState('');
  const [forwardEmail, setForwardEmail] = useState('');

  const [defaultAPAccountCode, setDefaultAPAccountCode] = useState('333');
  const [configRequired, setConfigRequired] = useState(false);
  const [emailAlertContent, setEmailAlertContent] = useState('');
  const [emailAlertType, setEmailAlertType] = useState('');
  const [showManagersEmailError, setShowManagersEmailError] = useState(false);
  const [showFinanceEmailError, setShowFinanceEmailError] = useState(false);
  const [showApproversEmailError, setShowApproversEmailError] = useState(false);
  const [showForwardEmailError, setShowForwardEmailError] = useState(false);

  const headers = {
    'Content-type': 'application/json',
    'x-user-email': 'integrations@arcanum.ai',
    'x-user-token': '62f4f964-1743-4667-88a3-6b2d2506a159',
  };

  const {
    get_config,
    wf_config,
    save_wf_config,
    fetchSetting,
    setFetchSetting,
    configAlertType,
    configAlertContent,
    wf_configSet,
  } = WorkflowConfig({
    combination_id: combination_id,
  });

  const emailFields = {
    managersEmail: {
      label: 'Supplier Notification Email',
      desc: 'When Archie creates a new supplier in Xero, he should inform:',
      value: managersEmail,
      showError: showManagersEmailError,
      setter: setManagersEmail,
    },
    approversEmail: {
      label: 'Supplier Approver Email',
      desc: 'When Archie receives an invoice from a new supplier, he should request approval from:',
      value: approversEmail,
      showError: showApproversEmailError,
      setter: setApproversEmail,
    },
    financeEmail: {
      label: 'Finance Email',
      desc: "When Archie can't take care of an email or invoice, he should forward it to the finance team:",
      value: financeEmail,
      showError: showFinanceEmailError,
      setter: setFinanceEmail,
    },
    forwardEmail: {
      label: 'Forward Email',
      desc: 'When Archie can’t take care of an email in the inbox, he should forward it to:',
      value: forwardEmail,
      showError: showForwardEmailError,
      setter: setForwardEmail,
    },
  };

  useEffect(() => {
    setSettingUpRecipe(recipeSetupStatus === 'complete' ? false : true);
    if (!sampleFileName1) {
      handleLoadSampleFiles();
      if (WFSettings.requiresConfigBeforeStart.includes(combination_id)) {
        setConfigRequired(true);
      }
      if (configRequired && !wf_configSet) {
        setRecipeDisabled(true);
      } else {
        setRecipeDisabled(false);
      }
    }

    if (wf_config && fetchSetting === 'get_config') {
      try {
        setManagersEmail(wf_config.variables.emails['MANAGERSEMAIL']);
        setApproversEmail(wf_config.variables.emails['APPROVERSEMAIL']);
        setFinanceEmail(wf_config.variables.emails['FINANCEEMAIL']);
        setForwardEmail(wf_config.variables.emails['FORWARDEMAIL']);
        setDefaultAPAccountCode(wf_config.variables.accountspayable['DEFAULTACCOUNTCODE']);
      } catch (error) {
        Console('issue getting all fields');
      }

      setFetchSetting('');
    }
  }, [recipeSetupStatus, configRequired, sampleFileName1, wf_config, wf_configSet, fetchSetting]);

  useEffect(() => {
    Console('first load');
    if (!wf_configSet) {
      get_config();
    }
  }, []);

  const handleEmailUpdate = (key, value) => {
    const field = emailFields[key];
    if (field) {
      field.setter(value);
    }
  };

  const renderEmailFields = () => {
    return Object.entries(emailFields).map(([key, field]) => (
      <>
        <label htmlFor={key}>{field.label}</label>
        <br />
        <span>{field.desc}</span>
        <br />
        <input
          type="text"
          id={key}
          value={field.value}
          onChange={(e) => handleEmailUpdate(key, e.target.value)}
          placeholder="email@domain.com"
          className="form-control input-2col"
        />
        <Alert show={field.showError} className="mt-3" variant={emailAlertType}>
          {emailAlertContent}
        </Alert>
        <hr />
      </>
    ));
  };

  const save_this_config = () => {
    setEmailAlertType('primary');
    setEmailAlertContent('This email address cannot be the same as the one Archie is using.');
    setShowManagersEmailError(false);
    setShowApproversEmailError(false);
    setShowFinanceEmailError(false);
    setShowForwardEmailError(false);

    const payload = {
      variables: {
        emails: {},
        accountspayable: {},
      },
    };
    const updatedEmails = Object.values(emailFields).map((field) => field.value);

    let connected_email = null;
    connectors.every((connector) => {
      connections_data.every((connection) => {
        if (connector?.provider === connection?.provider) {
          connected_email = connection.identity;
          return false;
        }
        return true;
      });
    });

    if (connected_email !== null && updatedEmails.includes(connected_email)) {
      if (managersEmail === connected_email) {
        setShowManagersEmailError(true);
      }
      if (approversEmail === connected_email) {
        setShowApproversEmailError(true);
      }
      if (financeEmail === connected_email) {
        setShowFinanceEmailError(true);
      }
      if (forwardEmail === connected_email) {
        setShowForwardEmailError(true);
      }
      return;
    }

    Object.entries(emailFields).forEach(([key, field]) => {
      const { value } = field;
      if (value) {
        payload.variables.emails[key.toUpperCase()] = value;
      }
    });

    if (defaultAPAccountCode) {
      payload.variables.accountspayable['DEFAULTACCOUNTCODE'] = defaultAPAccountCode;
    }

    save_wf_config(payload);
  };

  const handleLoadSampleFiles = async () => {
    const files = await getSampleFiles(combination_id);
    if (files !== null && files.length) {
      const [file_item1] = files;
      setSampleFileName1(file_item1.name);
      setSampleFile1(file_item1);
    }
  };

  const handleRunWithSample = async () => {
    const newname = sampleFile1.name.split('/').pop();
    setupFile(sampleFile1, newname);
  };

  const onFileChange = (e) => {
    const _file = e.target.files[0];
    if (check_file(_file)) {
      setupFile(_file, _file.name);
    }
  };

  const setupFile = (_file, filename) => {
    arc_tab_ref1.current.click();
    setSelectedFileName(filename);
    const fileReader = new FileReader();
    fileReader.readAsDataURL(_file);
    fileReader.onload = () => {
      if (fileReader !== null) {
        setSelectedFile(fileReader.result);
      }
    };
  };

  const check_file = (file) => {
    const allowedTypes = ['image/jpeg', 'image/png', 'image/tif', 'application/pdf'];

    if (!file) {
      alert('Please select a file');
      return;
    }

    const maxSize = 15 * 1024 * 1024; // 15 megabytes
    if (file && file.size > maxSize) {
      alert('File size too large');
      return;
    }

    if (!allowedTypes.includes(file.type)) {
      alert('File type not supported');
      return;
    }
    return true;
  };

  const stopJobStatusPoll = () => {
    clearInterval(jobStatusIntervalId);
  };

  const run_workflow = () => {
    setResetStatuses(true);

    let file_to_upload = '';
    file_to_upload = selectedFile.replace('data:image/png;base64,', '');
    file_to_upload = file_to_upload.replace('data:application/pdf;base64,', '');
    let payload;

    payload = {
      email: email,
      name: selectedFileName,
      binary: file_to_upload,
    };

    axios({
      method: 'post',
      url: webhook_url,
      data: payload,
    })
      .then((response) => {
        if (response.status === 200) {
          //setUploadResp(true);
          arc_tab_ref2.current.click();
          // run the lookup job status function
          if (WJST_id) {
            let this_id = setInterval(() => {
              // check job loop
              checkWorkatoJobStatus(
                `/api/platform/proxy?url=api/managed_users/${workato_id}/lookup_tables/${WJST_id}/rows`,
                'GET',
                null,
                headers,
              );
              setResetStatuses(false);
            }, 5000);
            setJobStatusIntervalId(this_id);
          }
        }
      })
      .catch((err) => {
        console.log(err);
      });
  };

  return (
    <>
      {!manual
        ? triggers.map((trigger) => {
            return activeTab === 'wf-trigger-' + trigger.id ? (
              <Col key={'trigger-info-' + trigger.id}>
                <>
                  {settingUpRecipe && connected ? (
                    <>
                      <Preloader smallscreen={true} />
                      <br />
                      <h4 className="text-center">Getting your workflow ready</h4>
                      <p className="text-center">This will just take a few seconds.</p>
                    </>
                  ) : settingUpRecipe && !connected ? (
                    <>
                      <br />
                      <h4 className="text-center">Let's Get Started</h4>
                      <br />
                      <p className="text-center">Please setup your connectors on the left.</p>
                    </>
                  ) : !recipeActive ? (
                    <>
                      <br />
                      <h4 className="text-center">Workflow Stopped</h4>
                      <br />
                      {configRequired && !wf_configSet ? (
                        <p className="text-center">Please setup the configuration in the next step.</p>
                      ) : (
                        <p className="text-center">Please start your workflow to begin.</p>
                      )}
                    </>
                  ) : (
                    <>
                      <header>
                        <img src={`/images/${trigger.icon}`} className="icon-solutions float-left" alt={trigger.name} />
                      </header>
                      <div>{trigger.name}</div>
                    </>
                  )}
                </>
              </Col>
            ) : null;
          })
        : null}

      {connectors.map((connector, key) => {
        return activeTab === 'wf-conn-' + key ? (
          <Col className="basic-form" key={'conn-info' + connector?.id}>
            <>
              {!connected ? (
                <>
                  <br />
                  <h4 className="text-center">Let's Get Started</h4>
                  <br />
                  <p className="text-center">Please setup your connectors on the left.</p>
                </>
              ) : settingUpRecipe ? (
                <>
                  <Preloader smallscreen={true} />
                  <br />
                  <h4 className="text-center">Getting your workflow ready</h4>
                </>
              ) : connector?.provider === 'arcanum_ai' && (combination_id === 13 || combination_id === 15) ? (
                <>
                  <strong>Select Archie’s key points of contact.</strong>
                  <br /> <br />
                  {renderEmailFields()}
                  <p>
                    If Archie cannot determine which account code to use for new items he will use: 333. <br />
                    <small>
                      - Simply go to your chart of accounts in Xero and create the new Expense item 333.
                      <br />- If you need to use another account code for default, please get in touch.{' '}
                    </small>
                  </p>
                  <input
                    type="hidden"
                    value={defaultAPAccountCode}
                    onChange={(e) => setDefaultAPAccountCode(e.target.value)}
                    placeholder="333"
                    className="form-control input-2col"
                  />
                  <br />
                  <>
                    <Button
                      id="submit"
                      disabled={!managersEmail || !approversEmail || !financeEmail || !forwardEmail}
                      onClick={save_this_config}
                      className="btn btn-primary x-5"
                    >
                      Save Config
                    </Button>
                    <Alert className="mt-3" key={configAlertType} variant={configAlertType}>
                      {configAlertContent}
                    </Alert>
                  </>
                  {/* <small>Note: this account code needs to be set up in Xero first.</small> */}
                </>
              ) : !recipeActive ? (
                <>
                  <br />
                  <h4 className="text-center">Workflow Stopped</h4>
                  <br />
                  {configRequired && !wf_configSet ? (
                    <p className="text-center">Please setup the configuration in the next step.</p>
                  ) : (
                    <p className="text-center">Please start your workflow to begin.</p>
                  )}
                </>
              ) : recipeActive ? (
                <>
                  <header>
                    <img
                      src={`/images/${connector?.icon}`}
                      className="icon-solutions float-left"
                      alt={connector?.name}
                    />
                  </header>

                  {connector?.provider === 'google_drive' ? (
                    this_folder_id !== null ? (
                      <a
                        target={'_blank'}
                        rel="noopener"
                        href={`https://drive.google.com/drive/folders/${this_folder_id}`}
                      >
                        Go to Gdrive Folder
                      </a>
                    ) : null
                  ) : connector?.provider === 'manual_upload' ? (
                    /**
                     * Manual Upload file form
                     */
                    <>
                      <h3>Upload your file</h3>
                      <Row>
                        <Col>
                          <input type="file" onChange={onFileChange} value={selectedFile ? selectedFile.name : ''} />
                          <br />
                          <small>Note: pdf, png, tif, jpg file formats supported</small>
                          <SamepleElements
                            handleRunWithSample={handleRunWithSample}
                            sampleFileName1={sampleFileName1}
                          />
                        </Col>
                      </Row>
                    </>
                  ) : connector?.provider === 'outlook' && connector?.name.toLowerCase().includes('input') ? (
                    /**
                     * Outlook or 365 input
                     */
                    <>
                      <h3>Outlook Input</h3>
                      <p>Emails with invoices attached will be automatically processed.</p>
                      <br />
                    </>
                  ) : connector?.provider === 'arcanum_ai' ? (
                    <>
                      <h5>This connector identifies and extracts fields in a document, including tables.</h5>
                      <br />

                      {/* <strong>Setup Fields to Extract.</strong>
                      <br />
                      <br />
                      <label className="custom-tick">
                        <input type="checkbox" checked disabled />
                        <span className="checkmark purple-checkmark"></span>
                        &nbsp; Sellect All
                      </label>
                      <br />
                      <label className="custom-tick">
                        <input type="checkbox" checked disabled />
                        <span className="checkmark purple-checkmark"></span>
                        &nbsp; Vendor Name
                      </label>

                      <label className="custom-tick">
                        <input type="checkbox" checked disabled />
                        <span className="checkmark purple-checkmark"></span>
                        &nbsp; Invoice Number or Invoice Reciept ID
                      </label>
                      <label className="custom-tick">
                        <input type="checkbox" checked disabled />
                        <span className="checkmark purple-checkmark"></span>
                        &nbsp; Due Date
                      </label>
                      <label className="custom-tick">
                        <input type="checkbox" checked disabled />
                        <span className="checkmark purple-checkmark"></span>
                        &nbsp; Subtotal
                      </label>
                      <label className="custom-tick">
                        <input type="checkbox" checked disabled />
                        <span className="checkmark purple-checkmark"></span>
                        &nbsp; Tax
                      </label>
                      <label className="custom-tick">
                        <input type="checkbox" checked disabled />
                        <span className="checkmark purple-checkmark"></span>
                        &nbsp; Total
                      </label>
                      <label className="custom-tick">
                        <input type="checkbox" checked disabled />
                        <span className="checkmark purple-checkmark"></span>
                        &nbsp; Bank Account Number
                      </label>
                      <label className="custom-tick">
                        <input type="checkbox" checked disabled />
                        <span className="checkmark purple-checkmark"></span>
                        &nbsp; Order Number
                      </label>

                      <label className="custom-tick">
                        <input type="checkbox" checked disabled />
                        <span className="checkmark purple-checkmark"></span>
                        &nbsp; Order Reference
                      </label>

                      <label className="custom-tick">
                        <input type="checkbox" checked disabled />
                        <span className="checkmark purple-checkmark"></span>
                        &nbsp; Customer Code
                      </label>

                      <label className="custom-tick">
                        <input type="checkbox" checked disabled />
                        <span className="checkmark purple-checkmark"></span>
                        &nbsp; Vendor Address
                      </label>
                      <label className="custom-tick">
                        <input type="checkbox" checked disabled />
                        <span className="checkmark purple-checkmark"></span>
                        &nbsp; Bill-to Name
                      </label>
                      <label className="custom-tick">
                        <input type="checkbox" checked disabled />
                        <span className="checkmark purple-checkmark"></span>
                        &nbsp; Bill-to Address
                      </label>
                      <label className="custom-tick">
                        <input type="checkbox" checked disabled />
                        <span className="checkmark purple-checkmark"></span>
                        &nbsp; Property Address
                      </label>
                      <label className="custom-tick">
                        <input type="checkbox" checked disabled />
                        <span className="checkmark purple-checkmark"></span>
                        &nbsp; Phone
                      </label>
                      <label className="custom-tick">
                        <input type="checkbox" checked disabled />
                        <span className="checkmark purple-checkmark"></span>
                        &nbsp; Email
                      </label> */}
                      <br />
                      {combination_id !== 1 && combination_id !== 13 && combination_id !== 15 && (
                        <Button
                          variant="primary"
                          type="submit"
                          onClick={run_workflow}
                          className="mt-3 px-4"
                          disabled={!selectedFile}
                        >
                          Run Workflow
                        </Button>
                      )}
                    </>
                  ) : connector?.provider === 'google_sheets' ? (
                    this_sheet_id !== null ? (
                      <a
                        target={'_blank'}
                        rel="noopener"
                        href={`https://docs.google.com/spreadsheets/d/${this_sheet_id}`}
                      >
                        Go to Spreadsheet
                      </a>
                    ) : null
                  ) : connector?.provider === 'xero' && connector?.name.toLowerCase().includes('output') ? (
                    <>
                      <h3>Xero Output</h3>
                      <p>Once the Arcanum process has completed we will send the result to your Xero account.</p>
                      <StatusMessages
                        resetStatuses={resetStatuses}
                        lookupTableData={lookupTableData}
                        stopJobStatusPoll={stopJobStatusPoll}
                        WJST_id={WJST_id}
                        workato_recipe_id={workato_recipe_id}
                        refreshTheRecipe={refreshTheRecipe}
                      />
                    </>
                  ) : connector?.provider === 'manual_email' ? (
                    <>
                      <h3>Email Output</h3>
                      <p>Once the Arcanum process has completed we will send the result to your email inbox.</p>
                      <StatusMessages
                        resetStatuses={resetStatuses}
                        lookupTableData={lookupTableData}
                        stopJobStatusPoll={stopJobStatusPoll}
                        WJST_id={WJST_id}
                        workato_recipe_id={workato_recipe_id}
                        refreshTheRecipe={refreshTheRecipe}
                      />
                    </>
                  ) : (
                    <div>{connector?.name}</div>
                  )}
                </>
              ) : null}
            </>
          </Col>
        ) : null;
      })}
    </>
  );
};

export { InvoiceExtractionWorkFlow };
